import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useIMask } from 'react-imask'

import { ITextInputProps } from './ITextInputProps'
import { TextInput } from './TextInput'

interface Props extends ITextInputProps {
  mask?:
    | 'float'
    | 'integer'
    | 'money'
    | 'zipCode'
    | 'creditCard'
    | 'numberString'
    | 'monthYear'
    | 'phone'
    | 'cvv'
    | 'cpf'
    | 'date'
  onChange: (value: any) => void
}

export interface IMaskedInput {
  setValue: (value: any) => void
}

const Component: React.ForwardRefRenderFunction<IMaskedInput, Props> = (
  { value, onChange, mask, onBlur, ...props },
  forwardedRef,
) => {
  const opts = useMemo<any>(() => {
    switch (mask) {
      case 'money':
        return {
          mask: 'R$ num',
          blocks: {
            num: {
              mask: Number,
              scale: 2,
              padFractionalZeros: true,
              normalizeZeros: true,
              radix: ',',
              mapToRadix: ['.'],
              thousandsSeparator: '.',
            }
          }
        }
      case 'float':
        return {
          mask: Number,
          scale: 2,
          padFractionalZeros: false,
          normalizeZeros: false,
          radix: ',',
          mapToRadix: ['.'],
        }
      case 'integer':
      case 'numberString':
        return {
          mask: Number,
          scale: 0,
          signed: false,
          thousandsSeparator: '',
        }
      case 'cpf':
        return { mask: '000.000.000-00' }
      case 'zipCode':
        return { mask: '00000-000' }
      case 'phone':
        return {
          mask: [
            { mask: '(00) 0000-0000' },
            { mask: '(00) 00000-0000' }
          ]
        }
      case 'creditCard':
        return { mask: '0000 0000 0000 0000' }
      case 'date':
        return { mask: '00/00/0000' }
      case 'monthYear':
        return { mask: '00/00' }
      case 'cvv':
        return { mask: '0000' }
      default:
        return { mask: /.*/ }
    }
  }, [mask])

  // Refs keep onAccept stable across renders so useIMask never rebinds the
  // mask mid-typing, and avoid a re-render between emitting a value and
  // receiving it back through props (which caused inputs to revert).
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const lastEmittedRef = useRef<any>(undefined)

  const handleAccept = useCallback(
    (v: string, maskRef: any) => {
      let emitValue: any = v

      if (mask === 'float' || mask === 'money') {
        const unmasked = maskRef.unmaskedValue
        if (unmasked) {
          const num = parseFloat(unmasked)
          emitValue = isNaN(num) ? 0 : num
        } else {
          emitValue = 0
        }
      } else if (mask === 'integer') {
        const unmasked = maskRef.unmaskedValue
        if (unmasked) {
          const num = parseInt(unmasked, 10)
          emitValue = isNaN(num) ? 0 : num
        } else {
          emitValue = 0
        }
      } else if (mask === 'numberString') {
        emitValue = maskRef.unmaskedValue
      } else {
        emitValue = v
      }

      lastEmittedRef.current = emitValue
      onChangeRef.current && onChangeRef.current(emitValue)
    },
    [mask]
  )

  const { ref: inputRef, setUnmaskedValue, setValue } = useIMask(opts, {
    onAccept: handleAccept,
  })

  // Sync external value changes into IMask if they differ from what we just emitted
  useEffect(() => {
    if (value === undefined || value === null) {
      if (lastEmittedRef.current !== undefined && lastEmittedRef.current !== null) {
        lastEmittedRef.current = value
        setValue('')
      }
      return
    }

    if (value !== lastEmittedRef.current) {
      const isNumericMask =
        mask === 'integer' || mask === 'float' || mask === 'money'

      // Zero means "not filled" for numeric inputs: keep the field empty so
      // the placeholder shows and typing doesn't append to a leading 0 ("05").
      // An empty field already emits 0, so the parent value stays consistent.
      if (isNumericMask && (value === 0 || value === '0')) {
        setValue('')
      } else if (mask === 'money' || mask === 'float') {
        setUnmaskedValue(String(value))
      } else {
        setValue(String(value))
      }
    }
  }, [value, mask, setValue, setUnmaskedValue])

  useImperativeHandle(forwardedRef, () => ({
    setValue(v: any) {
      if (mask === 'money' || mask === 'float') {
        setUnmaskedValue(String(v ?? ''))
      } else {
        setValue(String(v ?? ''))
      }
    },
  }))

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e)
    },
    [onBlur]
  )

  // The input must stay uncontrolled from React's point of view: IMask writes
  // to the DOM element directly, and a controlled value without onChange makes
  // React restore the stale rendered value over what the user just typed.
  return (
    <TextInput
      {...props}
      ref={inputRef as React.RefObject<HTMLInputElement>}
      onBlur={handleBlur}
    />
  )
}

export const MaskedInput = forwardRef(Component)
